import { useEffect, useState } from 'react';
import { BasePicker } from '../shared/BasePicker';
import type { LearningLesson } from '../../pages/Lessons/services/learningTypes';
import { adminLearningService } from '../../pages/Lessons/services/learningService';

interface LessonPickerProps {
    value: string;
    onChange: (sectionId: string, lesson: LearningLesson | null) => void;
    label?: string;
    required?: boolean;
    className?: string;
    usageMap?: Record<string, string[]>;
}

interface LessonWithSection {
    sectionId: string;
    lesson: LearningLesson;
}

export const LessonPicker = ({
    value,
    onChange,
    label = "Lesson",
    required = false,
    className = "",
    usageMap
}: LessonPickerProps) => {
    const [lessonsWithSections, setLessonsWithSections] = useState<LessonWithSection[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadLessons = async () => {
            try {
                setLoading(true);
                const sections = await adminLearningService.getAllSections();
                const fetchedLessons = sections.flatMap(section => section.lessons.map(lesson => ({ sectionId: section.uid, lesson })));
                setLessonsWithSections(fetchedLessons);
            } catch (error) {
                console.error('Failed to load lessons:', error);
            } finally {
                setLoading(false);
            }
        };

        loadLessons();
    }, []);

    return (
        <BasePicker<LessonWithSection>
            value={value}
            onChange={(lessonWithSection: LessonWithSection | null) => {
                if (lessonWithSection) {
                    onChange(lessonWithSection.sectionId, lessonWithSection.lesson);
                } else {
                    onChange('', null);
                }
            }}
            items={lessonsWithSections}
            config={{
                modalTitle: 'Select Lesson',
                searchPlaceholder: 'Search lessons...',
                emptyStateMessage: loading ? 'Loading lessons...' : 'No lessons available',
                noSelectionMessage: 'No lesson selected',
                getItemId: (lessonWithSection) => lessonWithSection.lesson.uid,
                getItemDisplayName: (lessonWithSection) => lessonWithSection.lesson.title,
                renderItemContent: (lessonWithSection) => (
                    <>
                        <div className="font-medium text-gray-900">
                            {lessonWithSection.lesson.title}
                        </div>
                        <div className="text-sm text-gray-500 mt-1 break-all">
                            ID: {lessonWithSection.lesson.uid}
                        </div>
                        <div className="text-sm text-gray-500 mt-1 break-all">
                            Section: {lessonWithSection.sectionId}
                        </div>
                        {lessonWithSection.lesson.description && (
                            <div className="text-sm text-gray-500 mt-1 truncate">
                                {lessonWithSection.lesson.description}
                            </div>
                        )}
                    </>
                ),
                usageMap
            }}
            label={label}
            required={required}
            className={className}
        />
    );
};
